import { PropTypes as AmktCommonPropTypes } from '@atlassian/amkt-common';
import { withFeatureFlags } from '@atlassian/mpac-ui';
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Provider } from 'react-redux';
import { DEV, PROD, STG } from "../common/constants/environments";
import CommonPropTypes from "../common/constants/proptypes/CommonPropTypes";
import { FeaturedAddonsConfig, FeaturedAddonsConfigPropType } from "../common/records/FeaturedAddonsConfig";
import SearchParamsUpdate, { SearchParamsUpdatePropType } from "../common/records/SearchParamsUpdate";
import configureStore from "../common/store/configureStore";
import { createAlgoliaClient } from "../common/utils/algoliaFactory";
import discoverCollections from "./config/discoverCollections";
import { DISCOVER } from "./constants/perspectives";
import AddonDiscoveryPropTypes from "./constants/proptypes/AddonDiscoveryPropTypes";
import AddonDiscoveryContainer from "./container/AddonDiscoveryContainer";
import reducer from "./reducer";

const App = props => {
  const store = useMemo(() => configureStore({
    debug: props.debug,
    reducer
  }), []);

  const getDiscoverCollectionsConfig = () => {
    const {
      enableJsdToJsm,
      enableCloudFortifiedAppsDiscovery
    } = props; // Options is a dynamic config passed to discoverCollections
    // to get dynamic filters configuration

    const filterOptions = {
      enableJsdToJsm,
      enableCloudFortifiedAppsDiscovery
    };
    return discoverCollections({
      featuredAddonsConfig: props.featuredAddonsConfig,
      options: filterOptions
    });
  };

  return /*#__PURE__*/React.createElement(Provider, {
    store: store
  }, /*#__PURE__*/React.createElement(AddonDiscoveryContainer, {
    discoverCollectionsConfig: getDiscoverCollectionsConfig(),
    algoliaClient: props.algoliaClient || createAlgoliaClient(props.algoliaConfig.toObject()),
    applyOverrides: props.applyOverrides,
    className: props.className,
    colSpan: props.colSpan,
    containerClassName: props.containerClassName,
    debug: props.debug,
    environment: props.environment,
    externalFacets: props.externalFacets,
    featuredContentOverride: props.featuredContentOverride,
    filtersContext: props.filtersContext,
    filters: props.filters,
    hiddenFilters: props.hiddenFilters,
    hideHeading: props.hideHeading,
    hitContext: props.hitContext,
    hideTextSearch: props.hideTextSearch,
    perspective: props.perspective,
    resolveAlgoliaQueryFilterOverrides: props.resolveAlgoliaQueryFilterOverrides,
    screenClass: props.screenClass,
    stateChangeRequested: props.stateChangeRequested,
    heroComponent: props.heroComponent,
    topPanel: props.topPanel,
    recommendationsPanel: props.recommendationsPanel,
    transitions: props.transitions,
    discoverHeading: props.discoverHeading,
    overrideLargeHitRightContent: props.overrideLargeHitRightContent,
    showBrowseHeading: props.showBrowseHeading
  }));
};

App.propTypes = {
  /**
   * Specifies if the filters are applied or not. Omits hidden filters
   */
  showBrowseHeading: PropTypes.bool,

  /**
   * A node to be injected for showing recommended apps
   */
  recommendationsPanel: PropTypes.node,

  /**
   * Algolia config, ie. Application and API keys
   *
   * shape: Immutable.Map({
   *      applicationId: string,
   *      apiKey: string
   * })
   *
   * Required if Algolia Client is not provided
   */
  algoliaConfig: AmktCommonPropTypes.requiredIf(CommonPropTypes.algoliaConfig, props => !props.algoliaClient),

  /**
   * Algolia search client for maintaining cache between searches
   * Provided by third-party algoliasearch library, and not dereferenced
   *
   * shape: any (but must have AlgoliaClient class name)
   *
   * Required if Algolia Config is not provided
   */
  algoliaClient: AmktCommonPropTypes.requiredIf(CommonPropTypes.algoliaClient, props => !props.algoliaConfig),

  /**
   * A SearchParamsUpdate where:
   * types: Immutable.Set<FacetType>
   * apply: (AlgoliaSearchHelper.SearchParameters) => AlgoliaSearchHelper.SearchParameters
   *
   * `types` lets you define a set of facets that aren't already known to Addon Discovery but are defined
   * in Algolia. `apply` lets you apply relevant filtering to the SearchParameters.
   */
  applyOverrides: SearchParamsUpdatePropType,

  /**
   * A class name to apply to the top-level element of Addon Discovery.
   */
  className: PropTypes.string,

  /**
   * Given a hit type (small tiles or large tiles), and a `screenClass`, how many
   * "columns" should a hit take up? 12 columns = 100% width, so setting "largeHits" and
   * "xl" to `4` means that on the largest screens, there will be 3 hits per row.
   * If specified, this property should contain every combination of hit type and `screenClass`.
   *
   * shape: Immutable.Map({
   *      xl: number (optional),
   *      lg: number (optional),
   *      md: number (optional),
   *      sm: number (optional),
   *      xs: number (optional)
   * })
   */
  colSpan: CommonPropTypes.colSpan,

  /**
   * A class name to apply to the top-level element of Addon Discovery, not including the `heroComponent`.
   */
  containerClassName: PropTypes.string,

  /**
   * When true, will turn on debug mode.
   */
  debug: PropTypes.bool,

  /**
   * A node to be injected at the top of all perspectives, above the search box and facets.
   */
  discoverHeading: PropTypes.node,

  /**
   * Which environment we are running in.
   */
  environment: PropTypes.oneOf([DEV, STG, PROD]).isRequired,

  /**
   * A node to be injected underneath the facets. Typically used when you want to add custom links.
   */
  externalFacets: PropTypes.node,

  /**
   * Immutable.List<FeaturedContentOverride>
   * For each FeaturedContentOverride, we will always render the node from `render()` in place of the
   * tile at `index`.
   */
  featuredContentOverride: CommonPropTypes.contentOverrides,

  /**
   * Immutable.Map<string, Immutable.Set<string>>
   * "Staff picks" for each product. The key represents a product key (`jira`, `bitbucket` etc.) and
   * the value is a set of 6 app keys in Marketplace. Not all supported products need to be specified.
   */
  featuredAddonsConfig: FeaturedAddonsConfigPropType,

  /**
   * Immutable.Map({
   *   discoverFacetHrefResolver: ({ filters: Filters }) => string
   *   searchFacetHrefResolver: ({ filters: Filters }) => string
   * })
   * Functions that each take filters and return a URL that, when visited, displays the same view
   * but with the given filters pre-applied.
   * Each returned URL will be set as the `href` on the corresponding filter link, so that new-tab-openers
   * can still have filters applied, and search engines can crawl and index your filters.
   */
  filtersContext: AddonDiscoveryPropTypes.filtersContext.isRequired,

  /**
   * Immutable.Map<string, string | Immutable.Set<string>>
   * The filters to apply to the search facets. Each key corresponds to a facet name.
   * Accepts "unenhanced" filters, meaning that for filters that accept
   * multiple values, it will also accept a single value.
   */
  filters: CommonPropTypes.unenhancedFilters,
  heroComponent: PropTypes.func,

  /**
   * Immutable.Map<string, bool | Immutable.Set<string>>
   * Configure which facets are not visible or editable by the user. For example, if embedding in JIRA,
   * you don't want the user to select a different product.
   * With the key being a facet (eg. `product` or `sort`), set the value to `true` to hide the
   * whole facet, or to a set of strings to hide just the facet values within that set.
   */
  hiddenFilters: AddonDiscoveryPropTypes.hiddenFilters,

  /**
   * When true, it will hide the default heading at the top of both the discover and search perspectives.
   */
  hideHeading: PropTypes.bool,

  /**
   * When true, it will hide the default search textbox.
   */
  hideTextSearch: PropTypes.bool,

  /**
   * A map of callbacks for when hit-related actions occur such as click or mount, plus
   * functions for resolving relative URLs in search results into absolute URLs.
   *
   * shape: Immutable.Map({
   *      hitHrefResolver: function (optional),
   *      hitImageHrefResolver: function,
   *      onHitClick: function,
   *      onHitMount: function (optional),
   *      onRatingsClick: function (optional)
   * })
   */
  hitContext: CommonPropTypes.hitContext.isRequired,

  /**
   * The perspective to show. `discover` refers to the front page containing curated results or
   * multiple "top" searches. `search` shows only search results.
   */
  perspective: AddonDiscoveryPropTypes.perspective,

  /**
   * Immutable.Map<string,
   *     ({ params: AlgoliaSearchHelper.SearchParameters, filters: Filters }) => AlgoliaSearchHelper.SearchParameters>
   * With the key being a facet, each function allows low-level modifications to the SearchParameters sent to Algolia.
   */
  resolveAlgoliaQueryFilterOverrides: CommonPropTypes.resolveAlgoliaQueryFilterOverrides,

  /**
   * "xl" | "lg" | "md" | "sm" | "xs"
   * Which "responsive view" to render, based on browser window size. Generally, you will use
   * ScreenClassRender from react-grid-system and pass in the value from that.
   */
  screenClass: CommonPropTypes.screenClass,

  /**
   * ({ perspective, filters }) => void
   * Addon Discovery is stateless. Use this callback to listen for when Addon Discovery requests any changes in state.
   * It's up to the embedder to update our props as it sees fit whenever we request a state change.
   */
  stateChangeRequested: PropTypes.func.isRequired,

  /**
   * A node to be injected at the top of the discover or search perspective, and beside the facets.
   */
  topPanel: PropTypes.node,

  /**
   * A map of callbacks for when a view transition starts or ends.
   *
   * shape: Immutable.Map({
   *      transitionStarted: function (optional),
   *      transitionComplete: function (optional)
   * })
   */
  transitions: CommonPropTypes.transitions,

  /**
   * (appkey: string) => ?React.Node
   *  This callback used to override LargeHit right content
   *  Returns React.Node to override
   */
  overrideLargeHitRightContent: PropTypes.func,
  enableJsdToJsm: PropTypes.bool,
  enableCloudFortifiedAppsDiscovery: PropTypes.bool
};
App.defaultProps = {
  applyOverrides: SearchParamsUpdate.default_(),
  colSpan: Immutable.Map(),
  debug: false,
  filters: Immutable.Map(),
  hiddenFilters: Immutable.Map(),
  hideHeading: false,
  hideTextSearch: false,
  perspective: DISCOVER,
  screenClass: 'xl',
  featuredAddonsConfig: new FeaturedAddonsConfig()
};
export default withFeatureFlags(App);